import type { ReviewScope } from '../api/client';
import type { ReviewGrade, ReviewItem, ReviewQueueMode, ReviewStep, UciMove } from '../api/types';

/**
 * Pure state machine for a line-walking review session (spec v1.7).
 *
 * Each item is one of the learner's own repertoire moves. The board first plays
 * the moves that lead to it (the server-supplied arrival path), then waits for the
 * learner. Consecutive items on the same line continue from the current board
 * instead of restarting, so a session feels like playing through lines.
 *
 * Grading is automatic and uses the FIRST attempt only:
 * - the drilled move → `correct` (or `incorrect` if the answer was revealed first);
 * - a different move that is also in the repertoire → a neutral retry, except that
 *   answering an alternative when the main move is drilled grades `alternative`;
 * - anything else → `incorrect`: the correct move is shown and must be played to
 *   continue, and the item is drilled once more at the end of the session.
 * New moves are taught with the answer on the board. Before the session leaves a
 * line, the moves it taught there come back as a quick test without arrows. Quick
 * tests and end-of-session re-drills are practice: only the first pass is graded.
 * The server owns the schedule; the client only reports what happened.
 */

export type SessionPhase = 'context' | 'prompt' | 'retry' | 'solved' | 'done';
export type MoveVerdict = 'correct' | 'sibling' | 'incorrect';

/**
 * - `drill`: a queue item, graded on its first attempt (and taught first when new);
 * - `check`: a quick test of a move just taught, played without arrows;
 * - `redrill`: a missed move, played once more at the end of the session.
 */
export type CardKind = 'drill' | 'check' | 'redrill';

export interface Card {
  item: ReviewItem;
  kind: CardKind;
}

export interface Attempt {
  uci: UciMove;
  verdict: Exclude<MoveVerdict, 'correct'>;
}

export interface GradeRecord {
  seq: number;
  edgeId: string;
  grade: ReviewGrade;
  mode: 'learn' | 'review';
}

export interface SessionStats {
  correct: number;
  alternative: number;
  incorrect: number;
  skipped: number;
}

export interface SessionState {
  /** Everything the session plays, in order. Only `drill` cards are graded. */
  cards: Card[];
  /** The queue size: how many `drill` cards the session holds. */
  total: number;
  index: number;
  phase: SessionPhase;
  board: { fen: string; lastMove?: UciMove };
  pending: ReviewStep[];
  /** First-attempt outcome for the current card, once known. */
  grade: ReviewGrade | null;
  attempt: Attempt | null;
  revealed: boolean;
  /** New moves taught on the current line, waiting for their quick test. */
  taught: ReviewItem[];
  /** The item played just before the first of `taught`; the quick test replays from there. */
  taughtAfter: ReviewItem | null;
  missed: string[];
  stats: SessionStats;
  record: GradeRecord | null;
}

export type SessionAction =
  | { type: 'step' }
  | { type: 'skip-context' }
  | { type: 'move'; uci: UciMove }
  | { type: 'reveal' }
  | { type: 'skip' }
  | { type: 'advance' };

export function judgeMove(item: ReviewItem, uci: string): MoveVerdict {
  if (uci === item.uci) return 'correct';
  return item.siblings.some((sibling) => sibling.uci === uci) ? 'sibling' : 'incorrect';
}

export function currentCard(state: SessionState): Card | null {
  return state.cards[state.index] ?? null;
}

export function currentItem(state: SessionState): ReviewItem | null {
  return currentCard(state)?.item ?? null;
}

/** A new move on its graded pass: the answer is shown before the learner plays it. */
export function isTeaching(card: Card): boolean {
  return card.kind === 'drill' && card.item.isNew;
}

export interface SessionProgress {
  kind: CardKind;
  /** 1-based position of the current card within its round. */
  position: number;
  /** Cards in the current round (the queue size for `drill`). */
  count: number;
  /** Graded cards already behind the learner. */
  done: number;
}

/** "Move 3 of 10", "Quick test · 1 of 2", "Mistakes · 1 of 1". */
export function sessionProgress(state: SessionState): SessionProgress {
  const done = state.cards.slice(0, state.index).filter((card) => card.kind === 'drill').length;
  const kind = currentCard(state)?.kind ?? 'drill';
  if (kind === 'drill') return { kind, position: done + 1, count: state.total, done };
  let start = state.index;
  while (start > 0 && state.cards[start - 1].kind === kind) start -= 1;
  let end = state.index + 1;
  while (end < state.cards.length && state.cards[end].kind === kind) end += 1;
  return { kind, position: state.index - start + 1, count: end - start, done };
}

/** Whether `next` picks up exactly where `previous` left off, with no rewind. */
export function continuesLine(previous: ReviewItem, next: ReviewItem): boolean {
  if (previous.startFen !== next.startFen || next.path.length <= previous.path.length) return false;
  return previous.path.every((step, index) => next.path[index].edgeId === step.edgeId) &&
    next.path[previous.path.length].edgeId === previous.edgeId;
}

/**
 * Where the board starts for `next`. Items arrive in line order, so the next one
 * usually shares the start of the previous line: the board rewinds only to the
 * branch point (or simply continues) instead of replaying from move one.
 */
export function contextPlan(
  previous: ReviewItem | null,
  next: ReviewItem,
): { fen: string; lastMove?: UciMove; steps: ReviewStep[] } {
  if (previous === null || previous.startFen !== next.startFen) {
    return { fen: next.startFen, steps: next.path };
  }
  const played = [...previous.path.map((step) => step.edgeId), previous.edgeId];
  let shared = 0;
  while (shared < next.path.length && next.path[shared].edgeId === played[shared]) shared += 1;
  if (shared === 0) return { fen: next.startFen, steps: next.path };
  const at = next.path[shared - 1];
  return { fen: at.fen, lastMove: at.uci, steps: next.path.slice(shared) };
}

const EMPTY_STATS: SessionStats = { correct: 0, alternative: 0, incorrect: 0, skipped: 0 };

function enter(state: SessionState, index: number, previous: ReviewItem | null): SessionState {
  const card = state.cards[index];
  if (card === undefined) {
    return { ...state, index, phase: 'done', pending: [], attempt: null, grade: null };
  }
  const plan = contextPlan(previous, card.item);
  return {
    ...state,
    index,
    board: { fen: plan.fen, lastMove: plan.lastMove },
    pending: plan.steps,
    phase: plan.steps.length > 0 ? 'context' : 'prompt',
    grade: null,
    attempt: null,
    // New moves are taught first: the answer is on the board before the learner plays it.
    revealed: isTeaching(card),
  };
}

export function startSession(items: ReviewItem[]): SessionState {
  const initial: SessionState = {
    cards: items.map((item) => ({ item, kind: 'drill' })),
    total: items.length,
    index: 0,
    phase: 'done',
    board: { fen: items[0]?.startFen ?? '' },
    pending: [],
    grade: null,
    attempt: null,
    revealed: false,
    taught: [],
    taughtAfter: null,
    missed: [],
    stats: EMPTY_STATS,
    record: null,
  };
  return enter(initial, 0, null);
}

function withRecord(state: SessionState, card: Card, grade: ReviewGrade): SessionState {
  if (card.kind !== 'drill') return state;
  const key = grade === 'skip' ? 'skipped' : grade;
  return {
    ...state,
    stats: { ...state.stats, [key]: state.stats[key] + 1 },
    record: {
      seq: (state.record?.seq ?? 0) + 1,
      edgeId: card.item.edgeId,
      grade,
      mode: card.item.isNew ? 'learn' : 'review',
    },
  };
}

function next(state: SessionState): SessionState {
  const card = state.cards[state.index];
  const following = state.cards[state.index + 1];
  const at = state.index + 1;
  // Before the session leaves a line, the moves it taught there are played again
  // without arrows, starting from where their lesson started.
  const staysOnLine = following?.kind === 'drill' && continuesLine(card.item, following.item);
  if (state.taught.length > 0 && !staysOnLine) {
    const checks = state.taught.map((item): Card => ({ item, kind: 'check' }));
    const cards = [...state.cards.slice(0, at), ...checks, ...state.cards.slice(at)];
    return enter({ ...state, cards, taught: [], taughtAfter: null }, at, state.taughtAfter);
  }
  if (following === undefined && state.missed.length > 0 && !state.cards.some((c) => c.kind === 'redrill')) {
    const again = state.missed
      .map((edgeId) => state.cards.find((candidate) => candidate.item.edgeId === edgeId)?.item)
      .filter((item): item is ReviewItem => item !== undefined)
      .map((item): Card => ({ item, kind: 'redrill' }));
    return enter({ ...state, cards: [...state.cards, ...again] }, at, card.item);
  }
  return enter(state, at, card.item);
}

export function sessionReducer(state: SessionState, action: SessionAction): SessionState {
  const card = currentCard(state);
  if (card === null || state.phase === 'done') return state;
  const { item } = card;
  const teaching = isTeaching(card);
  switch (action.type) {
    case 'step': {
      if (state.phase !== 'context') return state;
      const [step, ...rest] = state.pending;
      if (step === undefined) return { ...state, phase: 'prompt' };
      return {
        ...state,
        board: { fen: step.fen, lastMove: step.uci },
        pending: rest,
        phase: rest.length > 0 ? 'context' : 'prompt',
      };
    }
    case 'skip-context': {
      if (state.phase !== 'context') return state;
      const last = state.pending[state.pending.length - 1];
      return {
        ...state,
        board: last ? { fen: last.fen, lastMove: last.uci } : state.board,
        pending: [],
        phase: 'prompt',
      };
    }
    case 'move': {
      if (state.phase !== 'prompt' && state.phase !== 'retry') return state;
      const verdict = judgeMove(item, action.uci);
      if (verdict === 'correct') {
        const grade: ReviewGrade = state.grade ?? (state.revealed && !teaching ? 'incorrect' : 'correct');
        const solved: SessionState = {
          ...state,
          phase: 'solved',
          board: { fen: item.targetFen, lastMove: item.uci },
          attempt: null,
          grade,
          missed: grade === 'incorrect' && card.kind !== 'redrill' && !state.missed.includes(item.edgeId)
            ? [...state.missed, item.edgeId]
            : state.missed,
          taught: teaching ? [...state.taught, item] : state.taught,
          taughtAfter: teaching && state.taught.length === 0
            ? (state.cards[state.index - 1]?.item ?? null)
            : state.taughtAfter,
        };
        return withRecord(solved, card, grade);
      }
      if (verdict === 'sibling') {
        const sibling = item.siblings.find((candidate) => candidate.uci === action.uci);
        const downgrade = item.role === 'main' && sibling?.role === 'alternative' ? 'alternative' : null;
        return { ...state, attempt: { uci: action.uci, verdict }, grade: state.grade ?? downgrade };
      }
      return {
        ...state,
        phase: 'retry',
        attempt: { uci: action.uci, verdict },
        grade: state.grade ?? 'incorrect',
        revealed: true,
      };
    }
    case 'reveal': {
      if (state.phase !== 'prompt' && state.phase !== 'context') return state;
      const last = state.pending[state.pending.length - 1];
      return {
        ...state,
        board: last ? { fen: last.fen, lastMove: last.uci } : state.board,
        pending: [],
        phase: 'prompt',
        revealed: true,
        grade: state.grade ?? (teaching ? null : 'incorrect'),
      };
    }
    case 'skip': {
      if (state.phase === 'solved') return next(state);
      // The first attempt grades: skipping after a wrong move or "Show move" is still
      // a miss, so it is recorded (and re-drilled) as one rather than as a free skip.
      if (state.grade === 'incorrect') {
        const missed = card.kind !== 'redrill' && !state.missed.includes(item.edgeId)
          ? [...state.missed, item.edgeId]
          : state.missed;
        return next(withRecord({ ...state, missed }, card, 'incorrect'));
      }
      return next(withRecord(state, card, 'skip'));
    }
    case 'advance':
      return state.phase === 'solved' ? next(state) : state;
    default:
      return state;
  }
}

/** Move numbers for a path that starts at `startFen` (the root need not be move 1). */
export function numberedMoves(
  startFen: string,
  sans: string[],
): Array<{ label: string; san: string }> {
  const fields = startFen.trim().split(/\s+/);
  let black = fields[1] === 'b';
  let number = Number.parseInt(fields[5] ?? '1', 10) || 1;
  return sans.map((san, index) => {
    const label = !black ? `${number}.` : index === 0 ? `${number}…` : '';
    if (black) number += 1;
    black = !black;
    return { label, san };
  });
}

/** "now", "in 3 hours", "tomorrow", "in 5 days" — for due dates. */
export function relativeDue(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'not scheduled';
  const ms = Date.parse(iso) - now;
  if (ms <= 60_000) return 'now';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(ms / 3_600_000);
  if (hours < 24) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(ms / 86_400_000);
  return days === 1 ? 'tomorrow' : `in ${days} days`;
}

/** Session sizes per mode: review everything due, learn in small batches. */
export const SESSION_LIMITS: Record<ReviewQueueMode, number> = { due: 50, new: 10, all: 30 };

/** `?from=<zobristHash>.<positionId>` narrows practice to one part of the tree. */
export function parseScope(value: string | null): ReviewScope | undefined {
  const match = value ? /^([0-9a-f]{32})\.([0-9]+)$/.exec(value) : null;
  return match ? { zobristHash: match[1], positionId: match[2] } : undefined;
}

/** Link to the practice page, optionally straight into a mode or below a position. */
export function reviewRoute(
  repertoireId: string,
  options: { mode?: ReviewQueueMode; scope?: ReviewScope } = {},
): string {
  const params = new URLSearchParams();
  if (options.scope) params.set('from', `${options.scope.zobristHash}.${options.scope.positionId}`);
  if (options.mode) params.set('mode', options.mode);
  const query = params.toString();
  return `/repertoires/${repertoireId}/review${query ? `?${query}` : ''}`;
}
